import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };
const sel = 'h-8 rounded bg-[#0A0A0A] border border-[#262626] text-white text-xs px-1.5';
const CONF = { high: 'text-emerald-300', medium: 'text-amber-300', low: 'text-[#A3A3A3]', none: 'text-[#737373]' };

// iter317 — SUGGESTED mapping for unmapped Service Types. Nothing saves until the admin
// reviews, adjusts and clicks Confirm; unclear rows stay Unassigned (unchecked).
export const MappingSuggestionsDialog = ({ open, onOpenChange, trades, divisions, hideTest, onApplied }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/admin/service-types/mapping-suggestions`, opts)
      .then(({ data }) => setRows(data.suggestions.map((r) => ({ ...r, pick: false, department_id: r.suggested_department_id, division_id: r.suggested_division_id }))))
      .catch(() => toast.error('Could not load suggestions'))
      .finally(() => setLoading(false));
  }, [open]);
  const shown = rows.filter((r) => !(hideTest && r.is_test));
  const patch = (id, p) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const picked = shown.filter((r) => r.pick && r.department_id && r.division_id);
  const confirm = async () => {
    setSaving(true);
    const groups = {};
    picked.forEach((r) => { const k = `${r.department_id}|${r.division_id}`; (groups[k] = groups[k] || []).push(r.id); });
    try {
      await Promise.all(Object.entries(groups).map(([k, ids]) => {
        const [department_id, division_id] = k.split('|');
        return axios.post(`${API}/admin/service-types/bulk-assign`, { ids, department_id, division_id }, opts);
      }));
      toast.success(`Mapped ${picked.length} service type${picked.length === 1 ? '' : 's'}`);
      onApplied?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Mapping failed'); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="st-suggest-dialog" className="bg-[#141414] border-[#262626] text-white max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#D4AF37]" /> Suggested mapping — review before saving</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">These are suggestions from each name. Tick the rows you agree with, adjust Trade/Division if needed, then Confirm. Unticked rows stay Unassigned.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 text-xs">
          <Button data-testid="st-suggest-check-high" size="sm" variant="outline" className="border-[#262626] text-white" onClick={() => setRows((p) => p.map((r) => (r.confidence === 'high' && !(hideTest && r.is_test) ? { ...r, pick: true } : r)))}>Tick all high-confidence</Button>
          <Button data-testid="st-suggest-clear" size="sm" variant="outline" className="border-[#262626] text-white" onClick={() => setRows((p) => p.map((r) => ({ ...r, pick: false })))}>Clear</Button>
        </div>
        <div className="overflow-y-auto flex-1 border border-[#262626] rounded-md">
          {loading && <p className="p-4 text-sm text-[#A3A3A3]">Loading…</p>}
          {!loading && shown.length === 0 && <p className="p-4 text-sm text-[#A3A3A3]">Every service type is mapped.</p>}
          {shown.map((r) => (
            <div key={r.id} data-testid={`st-suggest-row-${r.id}`} className="grid grid-cols-[24px_1fr_130px_150px] md:grid-cols-[24px_1fr_130px_150px_140px] gap-2 items-center px-3 py-1.5 border-t border-[#262626] first:border-t-0">
              <input type="checkbox" data-testid={`st-suggest-pick-${r.id}`} checked={r.pick} disabled={!r.department_id || !r.division_id} onChange={(e) => patch(r.id, { pick: e.target.checked })} />
              <span className="text-sm truncate">{r.name}</span>
              <select data-testid={`st-suggest-trade-${r.id}`} value={r.department_id} onChange={(e) => patch(r.id, { department_id: e.target.value, division_id: '' })} className={sel}>
                <option value="">Unassigned</option>
                {trades.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select data-testid={`st-suggest-division-${r.id}`} value={r.division_id} disabled={!r.department_id} onChange={(e) => patch(r.id, { division_id: e.target.value })} className={`${sel} disabled:opacity-50`}>
                <option value="">Unassigned</option>
                {divisions.filter((d) => d.trade_id === r.department_id && d.active).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              <span className={`hidden md:block text-[10px] ${CONF[r.confidence]}`} title={r.reason}>{r.confidence === 'none' ? 'No suggestion' : `Suggested · ${r.confidence}`}</span>
            </div>
          ))}
        </div>
        <Button data-testid="st-suggest-confirm" disabled={saving || !picked.length} onClick={confirm} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
          {saving ? 'Saving…' : `Confirm mapping for ${picked.length} selected`}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default MappingSuggestionsDialog;
